import { useAuth } from "../../lib/auth";
import { useEffect, useState } from "react";
import { ApiError, assistanceApi, cancelInFlightApiRequests } from "../../lib/api";
import { clearAssistance, getAssistance, saveAssistance } from "../../lib/admin-assistance";

export function AssistOwnerControl({ providerId, approved, onStarted = () => window.location.assign("/provider/venue-setup") }: {
  providerId: string; approved: boolean; onStarted?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return <div className="grid gap-3">
    <button className="ops-button" data-variant="primary" type="button" disabled={!approved || busy} onClick={() => setOpen(!open)}>
      Assist owner
    </button>
    {!approved && <p className="text-xs text-[var(--color-text-soft)]">Approve the business account to prepare its workspace.</p>}
    {open && <form className="grid gap-3" onSubmit={async event => {
      event.preventDefault(); setBusy(true); setError(null);
      try {
        const session = await assistanceApi.start(providerId, reason.trim());
        saveAssistance(session);
        cancelInFlightApiRequests();
        onStarted();
      } catch (error) { setError(error instanceof Error ? error.message : "Unable to start assistance."); setBusy(false); }
    }}>
      <label className="grid gap-2 text-sm">Reason for assisting
        <textarea className="ops-input" minLength={10} maxLength={500} required value={reason} onChange={event => setReason(event.target.value)} placeholder="Prepare the venue for its first login" />
      </label>
      <p className="text-xs text-[var(--color-text-soft)]">Work in this owner's real workspace for up to 60 minutes. Changes are saved and attributed to you.</p>
      {error && <p role="alert" className="text-rose-400">{error}</p>}
      <button className="ops-button" type="submit" disabled={busy || reason.trim().length < 10}>{busy ? "Opening workspace…" : "Enter workspace"}</button>
    </form>}
  </div>;
}

export function AssistanceBanner({ onExited = () => window.location.assign("/admin") }: { onExited?: () => void }) {
  useAuth(); // Rerender when identity restoration clears an invalid assistance grant.
  const session = getAssistance();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!session) return;
    const remaining = Date.parse(session.expires_at) - Date.now();
    const timer = window.setTimeout(() => {
      clearAssistance(); cancelInFlightApiRequests(); onExited();
    }, Math.max(0, remaining));
    return () => window.clearTimeout(timer);
  }, [session?.id, session?.expires_at, onExited]);
  if (!session) return null;
  return <aside role="region" aria-label="Admin assistance" className="sticky top-0 z-[1000] flex flex-wrap items-center justify-between gap-3 border-b border-amber-400/40 bg-amber-950 px-5 py-3 text-amber-50">
    <div><p className="font-semibold">Assisting {session.provider_name}</p>
      <p className="text-xs">Acting as admin {session.actor_name} · Changes are saved immediately · Ends {new Date(session.expires_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
      {error && <p role="alert" className="text-sm">{error}</p>}
    </div>
    <button type="button" className="ops-button" disabled={busy} onClick={async () => {
      setBusy(true); setError(null);
      try {
        await assistanceApi.end(); clearAssistance(); cancelInFlightApiRequests(); onExited();
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          clearAssistance(); cancelInFlightApiRequests(); onExited();
          return;
        }
        setError("Could not end assistance. Check your connection and retry."); setBusy(false);
      }
    }}>{busy ? "Leaving…" : "Exit to admin"}</button>
  </aside>;
}
